import React, { FunctionComponent, useLayoutEffect, useMemo, useState } from 'react';

import { Refresh } from '@yandex-360/iconostasis/dist/icons/Refresh';
import { Button } from '@yandex-360/orb-components/dist/components/Button';
import { Divider } from '@yandex-360/orb-components/dist/components/Divider';
import { Empty } from '@yandex-360/orb-components/dist/components/Empty';
import { ListItem } from '@yandex-360/orb-components/dist/components/ListItem';
import { Spinner } from '@yandex-360/orb-components/dist/components/Spinner';
import { Tabs } from '@yandex-360/orb-components/dist/components/Tabs';
import { merge } from 'lodash';
import { useSelector } from 'react-redux';

import { ScrollContainer } from '../../../../../../../components/scroll-container';
import { i18n } from '../../../../../../../helpers/i18n';
import { Role, RolePermissionMatrixSlice } from '../../../../../../../helpers/permissions';
import { useActions } from '../../../../../../../hooks';
import {
    loadPermissionsExtendedMatrixAction,
    PermissionsExtendedStatus,
    selectNonConfigurablePermissions,
    selectPermissionsExtendedHaveChangedFromDefault,
    selectPermissionsExtendedMatrix,
    selectPermissionsExtendedMatrixOverride,
    selectPermissionsExtendedStatus,
    setPermissionsExtendedMatrixOverrideRolePermission,
} from '../../../../../../permissions-extended';
import {
    applySettingsPermissionsExtendedAction,
    resetSettingsPermissionsExtendedAction,
    returnToDefaultSettingsPermissionsExtendedAction,
} from '../../../../../slice';
import { Heading } from '../../../heading';
import { Island } from '../../../island';
import { SettingsTumbler } from '../../../tumblers';
import { PERMISSIONS_PREPROCESSED_GROUPS, ROLE_TABS_PROPS } from './constants';
import css from './styles.css';
import { RolePermissionGroupProps, RoleTabsProps } from './types';

export const PermissionsExtended: FunctionComponent = () => {
    const { setMatrixOverrideRolePermission, resetMatrixOverride, returnToDefault, loadMatrix, applyMatrix } =
        useActions({
            setMatrixOverrideRolePermission: setPermissionsExtendedMatrixOverrideRolePermission,
            resetMatrixOverride: resetSettingsPermissionsExtendedAction,
            returnToDefault: returnToDefaultSettingsPermissionsExtendedAction,
            loadMatrix: loadPermissionsExtendedMatrixAction,
            applyMatrix: applySettingsPermissionsExtendedAction,
        });

    const [selectedRole, setSelectedRole] = useState<Role>(Role.ADMIN);
    const matrix = useSelector(selectPermissionsExtendedMatrix);
    const matrixOverride = useSelector(selectPermissionsExtendedMatrixOverride);
    const nonConfigurablePermissions = useSelector(selectNonConfigurablePermissions);
    const haveChangedFromDefault = useSelector(selectPermissionsExtendedHaveChangedFromDefault);
    const status = useSelector(selectPermissionsExtendedStatus);

    useLayoutEffect(() => {
        if (!status) {
            loadMatrix();
        }
    }, [loadMatrix, status]);

    const hasDiff = Boolean(matrixOverride);

    const groups = useMemo<RolePermissionGroupProps[]>(() => {
        const excludedPermissions = new Set(nonConfigurablePermissions);

        return PERMISSIONS_PREPROCESSED_GROUPS.reduce((result, group) => {
            const configurablePermissions = group.permissions
                .filter(({ permission }) => !excludedPermissions.has(permission))
                .map(({ labelKey: permLabelKey, permission }) => ({
                    label: i18n('settings-v2', permLabelKey),
                    permission,
                }));

            if (!configurablePermissions.length) {
                return result;
            }

            result.push({
                label: i18n('settings-v2', group.labelKey),
                permissions: configurablePermissions,
            });

            return result;
        }, [] as RolePermissionGroupProps[]);
    }, [nonConfigurablePermissions]);

    const roleTabs = useMemo<RoleTabsProps[]>(
        () =>
            ROLE_TABS_PROPS.map(({ contentKey, ...tab }) => ({
                ...tab,
                content: i18n('settings-v2', contentKey),
            })),
        []
    );

    const mergedMatrixSlice: RolePermissionMatrixSlice = merge(
        {},
        matrix[selectedRole],
        matrixOverride?.[selectedRole]
    );

    const body = (() => {
        switch (status) {
            case PermissionsExtendedStatus.MATRIX_LOADING:
                return <Empty height="100%" content={<Spinner size="xl" view="orb" progress />} />;
            case PermissionsExtendedStatus.INIT_ERROR:
                return (
                    <Empty
                        height="100%"
                        title={i18n('settings-v2', 'matrix-info-could-not-load')}
                        button={
                            <Button
                                view="neutral"
                                iconLeft={<Refresh variant="outline" size="sm" />}
                                onPress={() => loadMatrix()}
                            >
                                {i18n('settings-v2', 'reload-matrix')}
                            </Button>
                        }
                    />
                );
            default:
                return (
                    <>
                        <div className={css.tabsWrapper}>
                            <div className={css.headerWrapper}>
                                <Tabs
                                    size="md"
                                    view="orb"
                                    activeTab={selectedRole}
                                    tabs={roleTabs}
                                    onChange={setSelectedRole as (tabId: string) => void}
                                />
                            </div>
                        </div>
                        <ScrollContainer className={css.groupsWrapper}>
                            {groups.map(({ label, permissions }, idx) => (
                                <>
                                    <Heading title={label} typography="body-short-lg-regular" color="secondary" />
                                    {permissions.map(({ label, permission }) => (
                                        <SettingsTumbler
                                            key={label}
                                            label={label}
                                            checked={mergedMatrixSlice?.[permission] ?? false}
                                            disabled={
                                                status === PermissionsExtendedStatus.APPLY_MATRIX_LOADING ||
                                                status === PermissionsExtendedStatus.RESET_TO_DEFAULT_LOADING
                                            }
                                            onChange={(event) =>
                                                setMatrixOverrideRolePermission({
                                                    role: selectedRole,
                                                    rolePermission: permission,
                                                    value: event.target.checked,
                                                })
                                            }
                                        />
                                    ))}
                                    {idx < groups.length - 1 && (
                                        <div className={css.dividerWrapper}>
                                            <Divider className={css.divider} />
                                        </div>
                                    )}
                                </>
                            ))}
                        </ScrollContainer>
                        {hasDiff && (
                            <div className={css.actionBar}>
                                <Button
                                    view="neutral"
                                    isDisabled={
                                        status === PermissionsExtendedStatus.APPLY_MATRIX_LOADING ||
                                        status === PermissionsExtendedStatus.RESET_TO_DEFAULT_LOADING
                                    }
                                    onPress={() => resetMatrixOverride()}
                                >
                                    {i18n('settings-v2', 'permissions-extended-cancel')}
                                </Button>
                                <Button
                                    view="brand"
                                    isPending={status === PermissionsExtendedStatus.APPLY_MATRIX_LOADING}
                                    isDisabled={status === PermissionsExtendedStatus.RESET_TO_DEFAULT_LOADING}
                                    onPress={() => applyMatrix()}
                                >
                                    {i18n('settings-v2', 'permissions-extended-apply')}
                                </Button>
                            </div>
                        )}
                    </>
                );
        }
    })();

    return (
        <div className={css.container}>
            <Island className={css.presetsContainer}>
                <Heading title={i18n('settings-v2', 'permissions-extended-header')} />
                <ListItem
                    label={i18n('settings-v2', 'permissions-extended-by-default')}
                    interaction={false}
                    endSlot={
                        <Button
                            view="neutral"
                            isPending={status === PermissionsExtendedStatus.RESET_TO_DEFAULT_LOADING}
                            isDisabled={
                                status === PermissionsExtendedStatus.MATRIX_LOADING ||
                                status === PermissionsExtendedStatus.INIT_ERROR ||
                                status === PermissionsExtendedStatus.APPLY_MATRIX_LOADING ||
                                !haveChangedFromDefault
                            }
                            onPress={() => returnToDefault()}
                        >
                            {i18n('settings-v2', 'permissions-extended-by-default-return')}
                        </Button>
                    }
                />
            </Island>
            <Island className={css.matrixContainer}>{body}</Island>
        </div>
    );
};
